import { FC, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import BreadCrumb from "../../components/Breadcrumbs/Breadcrumbs";
import Invoice from '../../components/Invoice/Invoice';
import { Box, Button, CircularProgress, Container, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import jwt_decode from 'jwt-decode';
import AppBar from "@mui/material/AppBar";
import { fetchInvoiceById, fetchInvoicePreview } from '../../store/invoiceStore/invoiceThunk';
import { InvoicePreviewPayload } from "../../utils/invoice.types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import HomeHeader from "../SignIn/HomeHeader";
import LoadingButton from "@mui/lab/LoadingButton";
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import CustomizedEstimateMenus from "../../components/dropdownbutton/estimateActionMenu";
import { Utils, VocuherCategory, VocuherTypes } from "../../utils/common-utils";

// Preview Invoice page.
const InvoicePreview: FC = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const voucherType = searchParams.get("type") || '';
  const showInLine = true;

  const dispatch = useAppDispatch();
 
  const { isLoggedIn } = useAppSelector((state) => state.auth);
  const invoiceDetails = useAppSelector(state => state.invoices.invoiceDetails);

  const [pdfLocation, setPdfLocation] = useState<string>("");
  const [showPayButton, setShowPayButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefersh, setIsRefersh] = useState("");

  useEffect(() => {
    if (voucherType == VocuherCategory.PURCHASE) {
      setShowPayButton(true);
    }

    const getPreview = async () => {
      let reqObj: InvoicePreviewPayload = { invoiceId: invoiceId, templateId: templateId };
      let invoicePreviewResponse: any = await dispatch(fetchInvoicePreview(reqObj));
      let location = null;
      if (invoicePreviewResponse?.payload) {
        if (invoicePreviewResponse.payload.length > 0) {
          location = invoicePreviewResponse.payload[0].Location;
        } else {
          location = invoicePreviewResponse.payload.Location;
        }
        setPdfLocation(location);
      }
      setIsLoading(false);
    };

    const getInvoiceDetails = async () => {
      if (invoiceId) {
        dispatch(fetchInvoiceById(invoiceId));
      }
    }

    if (templateId) {
      getPreview();
      if(isLoggedIn) {getInvoiceDetails();}
    } else {
      if (invoiceId) {
        try {
          let token: any = jwt_decode(invoiceId);
          setPdfLocation(token.pdfLcation);
          setIsLoading(false);
        } catch (error) {
          console.log("Failed! Invalid Token", error);
        }
      }
    }
  }, [invoiceId, isRefersh])

  const actionChangeHandler = (data: any) => {
    setIsRefersh(data.isRefersh);
  }

  return (
      <>
        <Box sx={{ 
          backgroundColor: '#fafafb', 
          minHeight: '100vh',
          pt: { xs: 10, sm: 9, md: 9 }, // Increased padding to account for AppBar (56px + spacing)
          pb: 2
        }}>
          <Grid container spacing={2} sx={{ 
            p: { xs: 1, sm: 2 }
          }}>
          {/* Left side - PDF viewer */}
          <Grid item xs={12} md={8} sx={{ 
            order: { xs: 2, md: 1 },
            mt: { xs: 2, md: 0 } // Add margin top on mobile after header section
          }}>
            {(isLoading ? 
              <Box sx={{ 
                backgroundColor: '#fff', 
                borderRadius: '12px', 
                p: 3, 
                height: { xs: '60vh', md: '85vh' } 
              }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info"/>
                    <Typography sx={{ mt: 2 }}>Loading Preview ...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
              :
              <Box sx={{ 
                backgroundColor: '#fff', 
                borderRadius: '12px', 
                overflow: 'hidden', 
                height: { xs: '60vh', md: '85vh' } 
              }}>
                <object style={{ height: "100%", width: "100%" }} data={pdfLocation} type="application/pdf">
                </object>
              </Box>
            )}
          </Grid>
          
          {/* Right side - Header and Party Details */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
            {/* Header Section */}
            <Box sx={{ 
              borderRadius: '12px', 
              border: '1px solid #e9ecf1', 
              backgroundColor: '#fff', 
              mb: 2 
            }}>
              <Box sx={{ 
                bgcolor: '#e9ecf1', 
                p: { xs: 1.5, sm: 2 }, 
                borderTopLeftRadius: '12px', 
                borderTopRightRadius: '12px' 
              }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={1} 
                  alignItems={{ xs: 'stretch', sm: 'center' }} 
                  justifyContent="space-between"
                >
                  <Button
                    variant="contained"
                    startIcon={<NavigateBeforeOutlinedIcon />}
                    onClick={() => {
                      const cancelLink = invoiceDetails.voucherCategory == VocuherCategory.SALE ? `/sales` : `/purchases`;
                      navigate(cancelLink);
                    }}
                    sx={{ 
                      backgroundColor: '#f5f5f5', 
                      color: '#545454',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                    size="small"
                  >
                    Back
                  </Button>
                  <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    {invoiceDetails && <CustomizedEstimateMenus type={voucherType} row={invoiceDetails} actionChangeHandler={actionChangeHandler} showInlineAction={true}/>}
                  </Box>
                </Stack>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  {invoiceDetails.voucherType} Preview
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  {invoiceDetails.invoiceNumber} • {invoiceDetails.status}
                </Typography>
              </Box>
            </Box>
            
            {/* Party Details Section */}
            <Box sx={{ 
              borderRadius: '12px', 
              border: '1px solid #e9ecf1', 
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                p: { xs: 1.5, sm: 2 }, 
                borderTopLeftRadius: '12px', 
                borderTopRightRadius: '12px' 
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  Party Details
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={{ xs: 1.5, sm: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Party Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invoiceDetails?.customerVendorName || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Contact</Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.customerVendorPhone || 'No contact available'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.customerVendorEmail || 'No email available'}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Invoice Amount</Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'primary.main',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }}
                    >
                      ₹{invoiceDetails?.totalAmount?.toLocaleString('en-IN') || '0'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Due Date</Typography>
                    <Typography variant="body1">
                      {invoiceDetails?.dueDate ? new Date(invoiceDetails.dueDate).toLocaleDateString('en-IN') : 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500,
                        color: invoiceDetails?.paymentStatus === 'PAID' ? 'success.main' : 'warning.main'
                      }}
                    >
                      {invoiceDetails?.paymentStatus || 'PENDING'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Grid>
          </Grid>
        </Box>
        {/* <Grid container rowSpacing="24" columnSpacing="5">
          <Container component="main" >
            {!isLoggedIn &&
              <div style={{ "width": "70vw" }}>
                <div className="preview">
                  <HomeHeader />
                </div>
                <Box onClick={() => { navigate("/") }}
                  style={{ "float": "right", "marginTop": "-42" }}
                  sx={{ cursor: "pointer" }}>
                  <Button
                    disableElevation
                    sx={{ mt: "20px" }}
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary">
                    Login/Signup
                  </Button>
                </Box>
                <p style={{ "clear": "both" }} />
              </div>
            }
            <Box style={{ "width": "70vw" }}>
              <Stack direction="row"
                justifyContent="space-between"
                alignItems="baseline" sx={{ mb: { xs: 0.9, sm: 0.9 } }}
                style={{ "float": "right" }}>
                <Box onClick={() => { }}
                  sx={{ cursor: "pointer" }}
                >
                  {showPayButton &&
                    <Button
                      disableElevation
                      sx={{ mt: "20px" }}
                      size="large"
                      type="submit"
                      variant="contained"
                      color="primary">
                      Pay
                    </Button>}
                </Box>
              </Stack>
            </Box>
            <p style={{ "clear": "both" }}></p>
            

          </Container>
        </Grid> */}
      </>
    )
};

export default InvoicePreview;
